/**
 * Panel NSYS Diagnostics — vista técnico.
 *
 * Permite consultar la API NSYS para obtener:
 *  - Datos completos del dispositivo (DeviceInfo)
 *  - Check rápido de IMEI (ESN/SimLock/BasicInfo)
 *  - Certificado de borrado
 * Auto-verifica QC si ESN=CLEAN, SIM=Unlocked, FMIP=0, sin tests críticos fallidos.
 */
import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  ShieldCheck, ShieldAlert, RefreshCw, FileText, Battery, Smartphone,
  ExternalLink, AlertCircle, CheckCircle2, XCircle, Cpu,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

function StatusBadge({ ok, label, value }) {
  return (
    <div className="flex items-center justify-between p-2 rounded border bg-card">
      <span className="text-xs text-muted-foreground">{label}</span>
      <Badge className={`text-[10px] ${ok ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
        {value ?? '—'}
      </Badge>
    </div>
  );
}

export default function NsysDevicePanel({ orden, onRefresh }) {
  const [loading, setLoading] = useState('');
  const [data, setData] = useState(null);

  const imei = orden?.imei || orden?.dispositivo?.imei;
  const serial = orden?.dispositivo?.serial;

  useEffect(() => {
    api.get(`/nsys/orden/${orden.id}`).then(r => setData(r.data)).catch(() => {});
  }, [orden.id]);

  const handleFetch = async (momento = 'general') => {
    if (!imei && !serial) {
      toast.error('La OT no tiene IMEI ni Serial');
      return;
    }
    setLoading(`fetch-${momento}`);
    try {
      const { data: res } = await api.post('/nsys/device-info', {
        orden_id: orden.id, imei, serial_number: serial, momento,
      });
      const labelMom = momento === 'entrada' ? 'ENTRADA' : momento === 'salida' ? 'SALIDA' : '';
      toast.success(res.evaluation.verified
        ? `✓ Auto-verificado ${labelMom}`
        : `Datos NSYS ${labelMom} obtenidos (revisión pendiente)`);
      // Avisos extra de auto-validación / bloqueo del IMEI
      if (res.imei_auto_validado) {
        toast.success('🛡️ IMEI validado automáticamente vía NSYS', { duration: 5000 });
      }
      if (res.bloqueada_por_imei) {
        toast.error('⚠️ ORDEN BLOQUEADA: el IMEI leído por NSYS no coincide con el de la OT', { duration: 8000 });
      }
      const { data: full } = await api.get(`/nsys/orden/${orden.id}`);
      setData(full);
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error NSYS');
    } finally {
      setLoading('');
    }
  };

  const handleDiagnosticsCert = async () => {
    setLoading('diagcert');
    try {
      const { data: res } = await api.post('/nsys/diagnostics-certificate', {
        orden_id: orden.id, imei, serial_number: serial,
      });
      // Preferimos abrir el PDF descargado en nuestro servidor (sin CORS, con auth).
      // Si por lo que sea no se descargó, caemos al link directo de NSYS.
      const localUrl = res.pdf_local_url;
      const externalUrl = res.Link;
      if (localUrl) {
        const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${localUrl}`;
        // Abrir con token en header → usamos fetch + blob para evitar exponer token en URL
        try {
          const token = localStorage.getItem('token');
          const r = await fetch(fullUrl, { headers: { Authorization: `Bearer ${token}` } });
          const blob = await r.blob();
          window.open(URL.createObjectURL(blob), '_blank');
          toast.success('Informe NSYS abierto (PDF local)');
        } catch {
          window.open(externalUrl, '_blank');
          toast.success('Certificado oficial NSYS abierto');
        }
      } else if (externalUrl) {
        window.open(externalUrl, '_blank');
        toast.success('Certificado oficial NSYS abierto');
      }
      const { data: full } = await api.get(`/nsys/orden/${orden.id}`);
      setData(full);
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo obtener el certificado');
    } finally {
      setLoading('');
    }
  };

  const handleCheckImei = async (tipo) => {
    if (!imei) { toast.error('IMEI requerido'); return; }
    setLoading(`check-${tipo}`);
    try {
      const { data: res } = await api.post('/nsys/check-imei', { orden_id: orden.id, imei, tipo });
      toast.success(`Check IMEI tipo ${tipo} completado`);
      const { data: full } = await api.get(`/nsys/orden/${orden.id}`);
      setData(full);
      console.log('NSYS CheckImei result:', res);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error NSYS');
    } finally {
      setLoading('');
    }
  };

  const handlePartsStatus = async () => {
    setLoading('parts');
    try {
      await api.post('/nsys/parts-status', { orden_id: orden.id, imei, serial_number: serial });
      const { data: full } = await api.get(`/nsys/orden/${orden.id}`);
      setData(full);
      toast.success('Detección de piezas completada');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo consultar piezas');
    } finally {
      setLoading('');
    }
  };

  const evalData = data?.nsys_data?.evaluation;
  const summary = evalData?.summary;
  const verified = data?.qc_verificado_nsys;
  const fetchedAt = data?.nsys_data?.fetched_at;

  return (
    <Card data-testid="nsys-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Cpu className="w-5 h-5 text-primary" />
          Diagnóstico NSYS
          {verified === true && (
            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300">
              <ShieldCheck className="w-3 h-3 mr-1" />
              Auto-verificado
            </Badge>
          )}
          {verified === false && evalData && (
            <Badge className="bg-amber-100 text-amber-700 border-amber-300">
              <ShieldAlert className="w-3 h-3 mr-1" />
              Requiere revisión
            </Badge>
          )}
        </CardTitle>
        {imei && <p className="text-[11px] text-muted-foreground font-mono">IMEI: {imei}</p>}
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2 flex-wrap">
          <Button
            onClick={() => {
              if (data?.nsys_data_entrada) {
                if (!window.confirm(`Ya hay un diagnóstico de ENTRADA del ${new Date(data.nsys_data_entrada.fetched_at).toLocaleString('es-ES')}. ¿Re-ejecutar? (consume otra licencia NSYS)`)) return;
              }
              handleFetch('entrada');
            }}
            disabled={loading.startsWith('fetch')}
            size="sm"
            variant={data?.nsys_data_entrada ? 'outline' : 'default'}
            title={data?.nsys_data_entrada
              ? `Ya realizado: ${new Date(data.nsys_data_entrada.fetched_at).toLocaleString('es-ES')} (pulsa para re-ejecutar)`
              : 'Diagnóstico al recibir el dispositivo en el taller'}
            data-testid="nsys-fetch-entrada"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading === 'fetch-entrada' ? 'animate-spin' : ''}`} />
            {data?.nsys_data_entrada ? '✓ ENTRADA' : 'Diagnóstico ENTRADA'}
          </Button>
          <Button
            onClick={() => {
              if (!data?.nsys_data_entrada) {
                if (!window.confirm('Aún no se ha registrado el diagnóstico de ENTRADA. ¿Estás seguro de hacer SALIDA primero? (recomendado: hacer ENTRADA al recibir el dispositivo)')) return;
              } else if (data?.nsys_data_salida) {
                if (!window.confirm(`Ya hay un diagnóstico de SALIDA del ${new Date(data.nsys_data_salida.fetched_at).toLocaleString('es-ES')}. ¿Re-ejecutar? (consume otra licencia NSYS)`)) return;
              }
              handleFetch('salida');
            }}
            disabled={loading.startsWith('fetch')}
            size="sm"
            variant={data?.nsys_data_salida ? 'outline' : 'default'}
            title={data?.nsys_data_salida
              ? `Ya realizado: ${new Date(data.nsys_data_salida.fetched_at).toLocaleString('es-ES')} (pulsa para re-ejecutar)`
              : 'Diagnóstico tras finalizar la reparación, antes de entregar'}
            data-testid="nsys-fetch-salida"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading === 'fetch-salida' ? 'animate-spin' : ''}`} />
            {data?.nsys_data_salida ? '✓ SALIDA' : 'Diagnóstico SALIDA'}
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleCheckImei(1)} disabled={!imei || loading.startsWith('check')}>
            ESN
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleCheckImei(3)} disabled={!imei || loading.startsWith('check')}>
            SimLock
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleCheckImei(5)} disabled={!imei || loading.startsWith('check')}>
            Estado
          </Button>
          <Button variant="outline" size="sm" onClick={handleDiagnosticsCert} disabled={loading === 'diagcert'} data-testid="nsys-diag-cert">
            <FileText className="w-3.5 h-3.5 mr-1.5" />
            Certificado oficial
          </Button>
          <Button variant="outline" size="sm" onClick={handlePartsStatus} disabled={loading === 'parts'} data-testid="nsys-parts">
            <Smartphone className="w-3.5 h-3.5 mr-1.5" />
            Piezas
          </Button>
        </div>

        {/* Estado entrada/salida */}
        {(data?.nsys_data_entrada || data?.nsys_data_salida) && (
          <div className="grid grid-cols-2 gap-2">
            {data?.nsys_data_entrada && (
              <div className="p-2 rounded border bg-blue-50/50">
                <p className="text-[10px] font-medium text-blue-800">ENTRADA · {new Date(data.nsys_data_entrada.fetched_at).toLocaleString('es-ES')}</p>
                <p className="text-xs">Grado: <strong>{data.nsys_data_entrada.evaluation?.summary?.grade || '—'}</strong> · Bat: {data.nsys_data_entrada.evaluation?.summary?.battery_health || '—'}%</p>
              </div>
            )}
            {data?.nsys_data_salida && (
              <div className="p-2 rounded border bg-emerald-50/50">
                <p className="text-[10px] font-medium text-emerald-800">SALIDA · {new Date(data.nsys_data_salida.fetched_at).toLocaleString('es-ES')}</p>
                <p className="text-xs">Grado: <strong>{data.nsys_data_salida.evaluation?.summary?.grade || '—'}</strong> · Bat: {data.nsys_data_salida.evaluation?.summary?.battery_health || '—'}%</p>
              </div>
            )}
          </div>
        )}

        {!evalData && (
          <p className="text-xs text-muted-foreground py-4 text-center">
            Pulsa "Obtener datos NSYS" para consultar el diagnóstico del dispositivo
          </p>
        )}

        {summary && (
          <div className="space-y-3">
            {/* Resumen */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              <StatusBadge label="Grado" value={summary.grade} ok={!!summary.grade} />
              <StatusBadge
                label="Batería salud"
                value={summary.battery_health ? `${summary.battery_health}%` : null}
                ok={Number(summary.battery_health) >= 80}
              />
              <StatusBadge label="Ciclos batería" value={summary.battery_cycles} ok={Number(summary.battery_cycles) < 500} />
              <StatusBadge label="ESN" value={summary.esn} ok={summary.esn === 'CLEAN'} />
              <StatusBadge label="SimLock" value={summary.lock_status} ok={summary.lock_status === 'Unlocked'} />
              <StatusBadge label="FMIP/FRP" value={summary.fmip_frp || '—'} ok={!summary.fmip_frp || summary.fmip_frp === '0'} />
            </div>

            {/* Tests pass/fail */}
            <div className="flex items-center gap-3 p-3 bg-muted/30 rounded border">
              <div className="flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                <span className="text-sm font-medium">{summary.tests_passed} pasados</span>
              </div>
              <div className="flex items-center gap-1.5">
                <XCircle className="w-4 h-4 text-red-600" />
                <span className="text-sm font-medium">{summary.tests_failed} fallidos</span>
              </div>
              <span className="text-xs text-muted-foreground ml-auto">{summary.tests_total} tests totales</span>
            </div>

            {/* Tests fallidos detalle */}
            {evalData.failed_tests?.length > 0 && (
              <div className="p-3 bg-red-50 border border-red-200 rounded">
                <p className="text-xs font-medium text-red-700 mb-1.5 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" />
                  Tests críticos fallidos
                </p>
                <ul className="space-y-1">
                  {evalData.failed_tests.map((t, i) => (
                    <li key={i} className="text-xs text-red-700">
                      • <span className="font-mono">{t.item}</span> <span className="text-red-500">({t.group}/{t.subgroup})</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Razones de no-verificación */}
            {!evalData.verified && evalData.reasons?.length > 0 && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded">
                <p className="text-xs font-medium text-amber-800 mb-1">Motivos por los que requiere revisión:</p>
                <ul className="space-y-0.5">
                  {evalData.reasons.map((r, i) => (
                    <li key={i} className="text-xs text-amber-800">• {r}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Piezas no originales detectadas */}
            {data?.nsys_parts_status?.result && (() => {
              const parts = data.nsys_parts_status.result;
              const partsList = Array.isArray(parts) ? parts : (parts.Parts || parts.parts || []);
              if (!Array.isArray(partsList) || partsList.length === 0) {
                return (
                  <div className="p-3 bg-emerald-50 border border-emerald-200 rounded text-xs text-emerald-800">
                    ✓ No se han detectado piezas no originales.
                  </div>
                );
              }
              const nonOriginal = partsList.filter(p =>
                (p.IsOriginal === false || p.Original === false ||
                 (typeof p.Status === 'string' && /no.*orig|non.*orig|repla/i.test(p.Status)))
              );
              return (
                <div className="p-3 bg-amber-50 border border-amber-300 rounded">
                  <p className="text-xs font-medium text-amber-900 mb-1.5">
                    {nonOriginal.length > 0
                      ? `⚠️ ${nonOriginal.length} pieza(s) NO original(es) detectada(s):`
                      : `${partsList.length} pieza(s) analizada(s) — todas originales:`}
                  </p>
                  <ul className="space-y-0.5">
                    {partsList.map((p, i) => (
                      <li key={i} className="text-xs">
                        <span className="font-mono">{p.PartName || p.Part || p.Name || p.name}</span>
                        {' · '}
                        <span className={nonOriginal.includes(p) ? 'text-red-700 font-medium' : 'text-emerald-700'}>
                          {p.Status || (p.IsOriginal === false ? 'NO ORIGINAL' : 'Original')}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })()}

            {/* Certificado de borrado: oculto a petición del usuario (no se realiza borrado) */}

            <p className="text-[10px] text-muted-foreground">
              Consultado: {fetchedAt ? new Date(fetchedAt).toLocaleString('es-ES') : '—'}
              {data?.nsys_data?.fetched_by ? ` · por ${data.nsys_data.fetched_by}` : ''}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
